import { c as _c } from "react/compiler-runtime";
import { IcoUser16 } from '@onefootprint/icons';
import { cx } from 'class-variance-authority';
import { isValidElement, type ReactNode } from 'react';
type Column = {
  key: string;
  label: string;
  align?: 'left' | 'right';
};
type Row = Record<string, ReactNode>;
type DataTableProps = {
  title?: string;
  columns: Column[];
  rows: Row[];
};
const isPlainObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v) && !isValidElement(v);
const isCellEmpty = (v: ReactNode): boolean => {
  if (v == null) return true;
  if (typeof v === 'string') return v.trim().length === 0;
  return false;
};
const cellToString = (v: ReactNode): string => {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (isPlainObject(v)) {
    return String(v.label ?? v.value ?? v.text ?? '');
  }
  return '';
};
const PERSON_COLUMN_KEYS = new Set(['pep', 'pep_status', 'sanctions', 'adverse_media', 'watchlist', 'screening']);
const isPersonTable = (columns: Column[]) => {
  const keys = columns.map(c => c.key.toLowerCase());
  const hasName = keys.some(k => k === 'name' || k === 'entity' || k === 'subject');
  const hasScreening = keys.some(k => PERSON_COLUMN_KEYS.has(k));
  return hasName && hasScreening;
};
const PersonCards = t0 => {
  const $ = _c(21);
  const {
    title,
    columns,
    rows
  } = t0;
  let t1;
  if ($[0] !== columns) {
    t1 = columns.find(_temp);
    $[0] = columns;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const nameCol = t1;
  let t2;
  if ($[2] !== columns) {
    t2 = columns.find(_temp2);
    $[2] = columns;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const roleCol = t2;
  let t3;
  let t4;
  let t5;
  if ($[4] !== columns || $[5] !== nameCol || $[6] !== roleCol || $[7] !== rows || $[8] !== title) {
    let t6;
    if ($[12] !== nameCol || $[13] !== roleCol) {
      t6 = c_1 => c_1 !== nameCol && c_1 !== roleCol;
      $[12] = nameCol;
      $[13] = roleCol;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    const detailCols = columns.filter(t6);
    t3 = "flex flex-col gap-3 w-full";
    if ($[15] !== title) {
      t4 = title && <span className="text-label-2 text-primary fp-text">{title}</span>;
      $[15] = title;
      $[16] = t4;
    } else {
      t4 = $[16];
    }
    t5 = rows.map((row, i) => {
      const name = nameCol ? cellToString(row[nameCol.key]) : `Item ${i + 1}`;
      const role = roleCol ? cellToString(row[roleCol.key]) : "";
      return <div key={i} className="border border-solid border-tertiary rounded-md p-3 flex flex-col gap-3"><div className="flex items-center gap-1"><IcoUser16 className="shrink-0" color="primary" /><p className="min-w-0 break-words text-label-2 text-primary fp-text">{name}</p></div>{role && <p className="text-body-2 text-tertiary fp-text">{role}</p>}{detailCols.some(col_0 => !isCellEmpty(row[col_0.key])) && <div className="flex flex-col gap-1">{detailCols.map(col => {
            const val = cellToString(row[col.key]);
            if (!val) {
              return null;
            }
            return <div key={col.key} className="flex items-start gap-4 min-h-6"><p className="text-body-2 text-tertiary fp-text w-28 flex-none whitespace-nowrap">{col.label}</p><p className="text-body-2 text-primary fp-text flex-1 min-w-0 break-words text-right">{val}</p></div>;
          })}</div>}</div>;
    });
    $[4] = columns;
    $[5] = nameCol;
    $[6] = roleCol;
    $[7] = rows;
    $[8] = title;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
  }
  let t6;
  if ($[17] !== t3 || $[18] !== t4 || $[19] !== t5) {
    t6 = <div className={t3}>{t4}{t5}</div>;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  return t6;
};
const CellValue = t0 => {
  const $ = _c(8);
  const {
    value
  } = t0;
  if (value == null) {
    return null;
  }
  if (typeof value === "string") {
    let t1;
    if ($[0] !== value) {
      t1 = value ? <span className="text-body-2 text-primary fp-text">{value}</span> : null;
      $[0] = value;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    const t1 = String(value);
    let t2;
    if ($[2] !== t1) {
      t2 = <span className="text-body-2 text-primary fp-text">{t1}</span>;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  if (isPlainObject(value)) {
    const text = String(value.label ?? value.value ?? value.text ?? "");
    let t1;
    if ($[4] !== text) {
      t1 = text ? <span className="text-body-2 text-primary fp-text">{text}</span> : null;
      $[4] = text;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    return t1;
  }
  let t1;
  if ($[6] !== value) {
    t1 = <>{value}</>;
    $[6] = value;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
};
const DataTable = t0 => {
  const $ = _c(29);
  const {
    title,
    columns: t1,
    rows: t2
  } = t0;
  let t10;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== t1 || $[1] !== t2 || $[2] !== title) {
    t10 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const columns = t1 === undefined ? [] : t1;
      const rows = t2 === undefined ? [] : t2;
      if (isPersonTable(columns)) {
        t10 = <PersonCards title={title} columns={columns} rows={rows} />;
        break bb0;
      }
      const visibleColumns = columns.filter(col => rows.some(row => !isCellEmpty(row[col.key])));
      t8 = "flex flex-col gap-3 w-full";
      if ($[11] !== title) {
        t9 = title && <span className="text-label-2 text-primary fp-text">{title}</span>;
        $[11] = title;
        $[12] = t9;
      } else {
        t9 = $[12];
      }
      t7 = "w-full overflow-x-auto overscroll-x-none rounded-md border border-solid border-tertiary";
      t4 = "w-full border-collapse";
      const t11 = visibleColumns.length * 140;
      if ($[13] !== t11) {
        t5 = {
          minWidth: t11
        };
        $[13] = t11;
        $[14] = t5;
      } else {
        t5 = $[14];
      }
      t6 = <thead><tr className="border-b border-solid border-tertiary bg-secondary">{visibleColumns.map(_temp3)}</tr></thead>;
      t3 = rows.map((row_0, i) => <tr key={i} className={cx("bg-primary", i < rows.length - 1 && "border-b border-solid border-tertiary")}>{visibleColumns.map(col_1 => <td key={col_1.key} className={cx("px-4 py-3", col_1.align === "right" && "text-right")}><CellValue value={row_0[col_1.key]} /></td>)}</tr>);
    }
    $[0] = t1;
    $[1] = t2;
    $[2] = title;
    $[3] = t10;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
    $[9] = t8;
    $[10] = t9;
  } else {
    t10 = $[3];
    t3 = $[4];
    t4 = $[5];
    t5 = $[6];
    t6 = $[7];
    t7 = $[8];
    t8 = $[9];
    t9 = $[10];
  }
  if (t10 !== Symbol.for("react.early_return_sentinel")) {
    return t10;
  }
  let t11;
  if ($[15] !== t3) {
    t11 = <tbody>{t3}</tbody>;
    $[15] = t3;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== t11 || $[18] !== t4 || $[19] !== t5 || $[20] !== t6) {
    t12 = <table className={t4} style={t5}>{t6}{t11}</table>;
    $[17] = t11;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t12 || $[23] !== t7) {
    t13 = <div className={t7}>{t12}</div>;
    $[22] = t12;
    $[23] = t7;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t13 || $[26] !== t8 || $[27] !== t9) {
    t14 = <div className={t8}>{t9}{t13}</div>;
    $[25] = t13;
    $[26] = t8;
    $[27] = t9;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  return t14;
};
export default DataTable;
function _temp(c) {
  return ["name", "entity", "subject"].includes(c.key.toLowerCase());
}
function _temp2(c_0) {
  return c_0.key.toLowerCase() === "role";
}
function _temp3(col_0) {
  return <th key={col_0.key} className={cx("px-4 py-2.5 whitespace-nowrap", col_0.align === "right" ? "text-right" : "text-left")}><span className="text-caption-1 text-tertiary fp-text font-medium">{col_0.label}</span></th>;
}